import { Badge, COLORS, Layout, Typography } from '@community_dev/pixels'
import { RoleGroup } from '@community_dev/types/lib/api/v1/Roles'
import { useQuery } from '@tanstack/react-query'
import { Table, Tabs, Tag } from 'antd'
import sortBy from 'lodash/sortBy'
import { useState, type JSX } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { RoleVisibility } from 'api/capabilities'
import { getRoles, Role } from 'api/clients'
import { useAdminRoles } from 'components/AdminForm/queries'
import { QUERIES, STALE_TIME } from 'constants/queries'
import { ROUTES } from 'constants/routes'
import { getRoleLabel } from 'utils/roles'
import { route } from 'utils/router'

const StyledBadge = styled(Badge)`
  left: 8px;
  top: -4px;
  position: relative;
`

export function roleRoute(roleName: string): string {
  return route(ROUTES.CAPABILITIES.ROLE, { name: encodeURIComponent(roleName) })
}

export function Roles(): JSX.Element {
  const [visibility, setVisibility] = useState<RoleVisibility>(RoleVisibility.ADMIN)
  const adminRoles = useAdminRoles()
  const clientRoles = useQuery([QUERIES.ROLES], getRoles, { staleTime: STALE_TIME.FOREVER })
  const { data = [], isInitialLoading } = visibility === RoleVisibility.ADMIN ? adminRoles : clientRoles
  const roles = sortBy(data, getRoleLabel)

  return (
    <Layout padding="48px">
      <h1 style={{ display: 'inline-block' }}>Roles</h1>
      <StyledBadge>{roles.length}</StyledBadge>
      <Typography color={COLORS.SUBTEXT} marginBottom="24px" variant="body2">
        A Role is a set of Capabilities that a seat holds. Admin roles apply to Community staff in this dashboard.
        Client roles apply to seats in customer accounts.
      </Typography>
      <Tabs
        activeKey={visibility}
        items={[
          { key: RoleVisibility.ADMIN, label: 'Admin roles' },
          { key: RoleVisibility.PUBLIC, label: 'Client roles' },
        ]}
        onChange={(key) => setVisibility(key as RoleVisibility)}
      />
      <Table<Role>
        columns={[
          {
            title: 'Role',
            key: 'name',
            render: (_, role) => (
              <>
                <Link to={roleRoute(role.name)}>{getRoleLabel(role)}</Link>
                <Typography color={COLORS.SUBTEXT} variant="caption1">
                  {role.name}
                </Typography>
              </>
            ),
          },
          { title: 'Description', dataIndex: 'description', key: 'description' },
          {
            title: 'Group',
            key: 'group',
            render: (_, role) => <Tag>{role.group === RoleGroup.PRIMARY ? 'Primary' : 'Additional'}</Tag>,
          },
          {
            title: 'Protected',
            key: 'protected',
            render: (_, role) => (role.protected ? <Tag>Protected</Tag> : null),
          },
          {
            title: 'Capabilities',
            key: 'capabilities',
            render: (_, role) => role.capabilities.length,
          },
        ]}
        dataSource={roles}
        loading={isInitialLoading}
        pagination={false}
        rowKey="id"
      />
    </Layout>
  )
}
